import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr, dateStr } from "@/lib/format";
import { KPI, SectionHeader } from "@/components/ui-extras";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { HardHat, Truck, Plus, Wallet, Users2, Factory, PackageCheck, FileText, QrCode, ClipboardList, Download, Clock, Calculator, Trash2, CheckCircle2, XCircle, ShieldCheck, Hourglass } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const FACADE_TRADES = [
  "Glazier", "Aluminium Fabricator", "Sealant Applicator", "Facade Installer",
  "Structural Glazing Fitter", "Curtain Wall Installer", "ACP Cladder",
  "Louver Fitter", "CNC Operator", "Anodizing / Powder Coat", "Skylight Fitter",
];
const SUB_TRADES = [
  "Aluminium Fabrication (YKK)", "Structural Glazing (SSG)", "Unitized Curtain Wall",
  "Stick Curtain Wall", "ACP Cladding", "Louvers", "Spider Glazing", "Skylight",
  "Sealants & Silicone", "Anodizing / Powder Coating",
];
const WO_STATUS_COLOR = {
  design: "bg-slate-100 text-slate-800 border-slate-200",
  cutting: "bg-sky-100 text-sky-800 border-sky-200",
  machining: "bg-indigo-100 text-indigo-800 border-indigo-200",
  assembly: "bg-amber-100 text-amber-800 border-amber-200",
  glazing: "bg-cyan-100 text-cyan-800 border-cyan-200",
  qc: "bg-purple-100 text-purple-800 border-purple-200",
  dispatch: "bg-orange-100 text-orange-800 border-orange-200",
  installed: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

export default function LaborSub() {
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [labor, setLabor] = useState([]);
  const [subs, setSubs] = useState([]);
  const [bills, setBills] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [woMeta, setWoMeta] = useState({ systems: [], unit_types: [], statuses: [] });
  const [otEntries, setOtEntries] = useState([]);

  const load = async () => {
    const [p, s, m] = await Promise.all([
      api.get("/projects"), api.get("/subcontractors"), api.get("/work-orders/meta/enums"),
    ]);
    setProjects(p.data); setSubs(s.data); setWoMeta(m.data);
    if (p.data.length && !project) setProject(p.data[0]);
  };
  const loadForProject = async (pid) => {
    const [l, b, w, o] = await Promise.all([
      api.get(`/labor?project_id=${pid}`),
      api.get(`/sub-bills?project_id=${pid}`),
      api.get(`/work-orders?project_id=${pid}`),
      api.get(`/labor-ot?project_id=${pid}`),
    ]);
    setLabor(l.data); setBills(b.data); setWorkOrders(w.data); setOtEntries(o.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (project) loadForProject(project.project_id); }, [project]);

  const laborCost = labor.reduce((s, r) => s + r.total_cost, 0);
  const workerDays = labor.reduce((s, r) => s + r.num_workers, 0);
  const subPending = bills.filter((b) => b.status === "pending").reduce((s, r) => s + r.net_payable, 0);
  const woActive = workOrders.filter((w) => !["installed"].includes(w.status)).length;
  const woUnits = workOrders.reduce((s, r) => s + Number(r.quantity || 0), 0);

  return (
    <div className="space-y-6" data-testid="labor-root">
      <SectionHeader
        title="Site Execution & Aluminium Fabrication"
        subtitle="Facade site gangs, subcontractor bills and YKK Iwin work orders for the fabrication unit"
        right={
          <Select value={project?.project_id || ""} onValueChange={(v) => setProject(projects.find((p) => p.project_id === v))}>
            <SelectTrigger data-testid="labor-project-select" className="w-72 h-9"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.project_id} value={p.project_id}>
                  <span className="font-mono text-xs mr-2 text-muted-foreground">{p.code}</span>{p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI testId="labor-cost" label="Site labor cost" value={laborCost} compact icon={Wallet} />
        <KPI testId="labor-workerdays" label="Worker-days logged" value={workerDays} icon={Users2} />
        <KPI testId="sub-pending" label="Subcontractor payable" value={subPending} compact icon={Truck} tone="warn" />
        <KPI testId="wo-active" label="Active work orders" value={woActive} suffix={` / ${woUnits} units`} icon={Factory} />
      </div>

      <Tabs defaultValue="labor">
        <TabsList>
          <TabsTrigger value="labor" data-testid="tab-labor-log">Site labor log</TabsTrigger>
          <TabsTrigger value="subs" data-testid="tab-subs">Subcontractors</TabsTrigger>
          <TabsTrigger value="bills" data-testid="tab-sub-bills">Subcontractor bills</TabsTrigger>
          <TabsTrigger value="wo" data-testid="tab-work-orders">Fabrication (YKK Iwin)</TabsTrigger>
          <TabsTrigger value="ot" data-testid="tab-ot">Overtime</TabsTrigger>
        </TabsList>

        <TabsContent value="labor" className="mt-4">
          <Card className="shadow-none">
            <CardHeader className="pb-3 flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold font-display">Daily site gang log · facade trades</CardTitle>
              {project && <NewLaborDialog project={project} onCreated={() => loadForProject(project.project_id)} />}
            </CardHeader>
            <CardContent className="pt-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                    <th className="py-2 font-medium">Date</th>
                    <th className="py-2 font-medium">Gang</th>
                    <th className="py-2 font-medium">Trade</th>
                    <th className="py-2 font-medium">Supervisor</th>
                    <th className="py-2 font-medium text-right">Workers</th>
                    <th className="py-2 font-medium text-right">Rate/day</th>
                    <th className="py-2 font-medium text-right">Total cost</th>
                  </tr>
                </thead>
                <tbody>
                  {labor.map((l) => (
                    <tr key={l.labor_id} className="border-b hover:bg-accent/50">
                      <td className="py-2 text-xs">{dateStr(l.date)}</td>
                      <td className="py-2 font-mono text-xs">{l.gang_name}</td>
                      <td className="py-2"><Badge variant="outline">{l.trade}</Badge></td>
                      <td className="py-2 text-xs">{l.supervisor}</td>
                      <td className="py-2 cell-num">{l.num_workers}</td>
                      <td className="py-2 cell-num text-muted-foreground">{inr(l.rate_per_day)}</td>
                      <td className="py-2 cell-num font-medium">{inr(l.total_cost, { compact: true })}</td>
                    </tr>
                  ))}
                  {labor.length === 0 && (
                    <tr><td colSpan={7} className="py-8 text-center text-sm text-muted-foreground">No labor entries yet</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subs" className="mt-4">
          <Card className="shadow-none">
            <CardHeader className="pb-3 flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold font-display">Facade subcontractor master</CardTitle>
              <NewSubDialog onCreated={load} />
            </CardHeader>
            <CardContent className="pt-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                    <th className="py-2 font-medium">Name</th>
                    <th className="py-2 font-medium">Trade</th>
                    <th className="py-2 font-medium">Contact</th>
                    <th className="py-2 font-medium">Phone</th>
                    <th className="py-2 font-medium">GST No</th>
                    <th className="py-2 font-medium">PAN</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.sub_id} className="border-b hover:bg-accent/50">
                      <td className="py-2 font-medium">{s.name}</td>
                      <td className="py-2"><Badge variant="outline">{s.trade}</Badge></td>
                      <td className="py-2">{s.contact_person}</td>
                      <td className="py-2 font-mono text-xs">{s.phone}</td>
                      <td className="py-2 font-mono text-xs">{s.gst_no}</td>
                      <td className="py-2 font-mono text-xs">{s.pan}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bills" className="mt-4">
          <Card className="shadow-none">
            <CardHeader className="pb-3 flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold font-display">Subcontractor RA bills</CardTitle>
              {project && <NewSubBillDialog project={project} subs={subs} onCreated={() => loadForProject(project.project_id)} />}
            </CardHeader>
            <CardContent className="pt-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                    <th className="py-2 font-medium">Bill #</th>
                    <th className="py-2 font-medium">Date</th>
                    <th className="py-2 font-medium">Vendor</th>
                    <th className="py-2 font-medium">Work</th>
                    <th className="py-2 font-medium text-right">Base</th>
                    <th className="py-2 font-medium text-right">GST</th>
                    <th className="py-2 font-medium text-right">TDS</th>
                    <th className="py-2 font-medium text-right">Retention</th>
                    <th className="py-2 font-medium text-right">Net payable</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map((b) => {
                    const sub = subs.find((s) => s.sub_id === b.sub_id);
                    return (
                      <tr key={b.bill_id} className="border-b hover:bg-accent/50">
                        <td className="py-2 font-mono text-xs">{b.bill_no}</td>
                        <td className="py-2 text-xs">{dateStr(b.date)}</td>
                        <td className="py-2">{sub?.name || "—"}</td>
                        <td className="py-2 max-w-[200px] truncate">{b.work_description}</td>
                        <td className="py-2 cell-num">{inr(b.amount, { compact: true })}</td>
                        <td className="py-2 cell-num text-muted-foreground">{inr(b.gst_amount, { compact: true })}</td>
                        <td className="py-2 cell-num text-destructive">-{inr(b.tds_amount, { compact: true })}</td>
                        <td className="py-2 cell-num text-destructive">-{inr(b.retention_amount, { compact: true })}</td>
                        <td className="py-2 cell-num font-medium">{inr(b.net_payable, { compact: true })}</td>
                        <td className="py-2">
                          <Select value={b.status} onValueChange={async (v) => {
                            await api.patch(`/sub-bills/${b.bill_id}`, { status: v });
                            toast.success("Updated");
                            loadForProject(project.project_id);
                          }}>
                            <SelectTrigger className="h-7 w-24 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {["pending","approved","paid"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    );
                  })}
                  {bills.length === 0 && (
                    <tr><td colSpan={10} className="py-8 text-center text-sm text-muted-foreground">No bills yet</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="wo" className="mt-4 space-y-4">
          <Card className="shadow-none border-secondary/30 bg-secondary/5">
            <CardContent className="p-4 flex items-center gap-3">
              <Factory className="h-5 w-5 text-secondary shrink-0" />
              <div className="text-sm">
                <strong className="font-medium">Fabrication unit</strong> — dedicated shop for
                <span className="mx-1 font-mono">YKK AP Iwin</span> aluminium door &amp; window systems.
                Track each work order from design → cutting → machining → assembly → glazing → QC → dispatch → installed.
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader className="pb-3 flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold font-display flex items-center gap-2">
                <PackageCheck className="h-4 w-4" /> YKK Iwin work orders
              </CardTitle>
              <div className="flex gap-2">
                <PlannerButton />
                {project && <NewWorkOrderDialog project={project} meta={woMeta} onCreated={() => loadForProject(project.project_id)} />}
              </div>
            </CardHeader>
            <CardContent className="pt-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                    <th className="py-2 font-medium">WO #</th>
                    <th className="py-2 font-medium">System</th>
                    <th className="py-2 font-medium">Unit type</th>
                    <th className="py-2 font-medium text-right">Qty</th>
                    <th className="py-2 font-medium">Size (mm)</th>
                    <th className="py-2 font-medium">Glass spec</th>
                    <th className="py-2 font-medium">Target</th>
                    <th className="py-2 font-medium">Status</th>
                    <th className="py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {workOrders.map((w) => (
                    <tr key={w.work_order_id} className="border-b hover:bg-accent/50">
                      <td className="py-2 font-mono text-xs">{w.wo_no}</td>
                      <td className="py-2 text-xs">{w.system}</td>
                      <td className="py-2"><Badge variant="outline">{w.unit_type}</Badge></td>
                      <td className="py-2 cell-num">{w.quantity}</td>
                      <td className="py-2 text-xs font-mono">{w.size_mm}</td>
                      <td className="py-2 text-xs">{w.glass_spec}</td>
                      <td className="py-2 text-xs">{dateStr(w.target_dispatch)}</td>
                      <td className="py-2">
                        <Select value={w.status} onValueChange={async (v) => {
                          await api.patch(`/work-orders/${w.work_order_id}`, { status: v });
                          toast.success(`Moved to ${v}`);
                          loadForProject(project.project_id);
                        }}>
                          <SelectTrigger data-testid={`wo-status-${w.work_order_id}`} className={`h-7 w-28 text-xs ${WO_STATUS_COLOR[w.status] || ""}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {woMeta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-2">
                        <div className="flex gap-1">
                          <BOMButton wo={w} />
                          <LabelButton wo={w} />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {workOrders.length === 0 && (
                    <tr><td colSpan={9} className="py-8 text-center text-sm text-muted-foreground">No work orders yet</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ot" className="mt-4 space-y-4">
          <Card className="shadow-none border-secondary/30 bg-secondary/5">
            <CardContent className="p-4 flex items-start gap-3">
              <Clock className="h-5 w-5 text-secondary shrink-0 mt-0.5" />
              <div className="text-sm leading-relaxed">
                <strong className="font-medium">Overtime policy</strong> — Standard shift <span className="font-mono">08:30 – 18:00</span>
                with 2 × 15-minute breaks and a 60-minute lunch = <b>8 paid hours / day, 26 days / month</b>.
                <br />
                Per-hour rate = <span className="font-mono">Monthly ÷ 26 ÷ 8</span>. Overtime pay = <b>1.25 ×</b> per-hour rate × OT hours.
                Use <em>Manual OT</em> for exceptional cases where the formula does not apply.
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <KPI testId="ot-total" label="OT pay this project" value={otEntries.reduce((s, r) => s + Number(r.ot_pay || 0), 0)} compact icon={Wallet} tone="warn" />
            <KPI testId="ot-hours-total" label="Total OT hours" value={otEntries.reduce((s, r) => s + Number(r.ot_hours || 0), 0)} suffix=" hrs" icon={Clock} />
            <KPI testId="ot-workers" label="Workers on OT" value={new Set(otEntries.map((r) => r.worker_name)).size} icon={Users2} />
            <KPI testId="ot-manual" label="Manual OT entries" value={otEntries.filter((r) => r.mode === "manual").length} icon={Calculator} />
          </div>

          <Card className="shadow-none">
            <CardHeader className="pb-3 flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold font-display">Overtime log</CardTitle>
              {project && (
                <div className="flex gap-2">
                  <OTAutoDialog project={project} onCreated={() => loadForProject(project.project_id)} />
                  <OTManualDialog project={project} onCreated={() => loadForProject(project.project_id)} />
                </div>
              )}
            </CardHeader>
            <CardContent className="pt-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                    <th className="py-2 font-medium">Date</th>
                    <th className="py-2 font-medium">Worker</th>
                    <th className="py-2 font-medium">Trade</th>
                    <th className="py-2 font-medium">Mode</th>
                    <th className="py-2 font-medium">Status</th>
                    <th className="py-2 font-medium text-right">Salary/mo</th>
                    <th className="py-2 font-medium text-right">₹/hr</th>
                    <th className="py-2 font-medium text-right">OT ₹/hr (×1.25)</th>
                    <th className="py-2 font-medium text-right">OT hrs</th>
                    <th className="py-2 font-medium text-right">OT pay</th>
                    <th className="py-2 font-medium">Reason</th>
                    <th className="py-2 font-medium">Approvals</th>
                    <th className="py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {otEntries.map((r) => (
                    <tr key={r.ot_id} className="border-b hover:bg-accent/50" data-testid={`ot-row-${r.ot_id}`}>
                      <td className="py-2 text-xs">{dateStr(r.date)}</td>
                      <td className="py-2 font-medium">{r.worker_name}</td>
                      <td className="py-2"><Badge variant="outline">{r.trade}</Badge></td>
                      <td className="py-2">
                        <Badge className={r.mode === "manual" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-sky-100 text-sky-800 border-sky-200"}>
                          {r.mode}
                        </Badge>
                      </td>
                      <td className="py-2"><OTStatusBadge r={r} /></td>
                      <td className="py-2 cell-num text-muted-foreground">{r.mode === "manual" ? "—" : inr(r.monthly_salary, { compact: true })}</td>
                      <td className="py-2 cell-num text-muted-foreground">{r.mode === "manual" ? "—" : inr(r.per_hour_rate)}</td>
                      <td className="py-2 cell-num text-muted-foreground">{r.mode === "manual" ? "—" : inr(r.ot_hourly_rate)}</td>
                      <td className="py-2 cell-num">{r.ot_hours || "—"}</td>
                      <td className="py-2 cell-num font-semibold text-primary">{inr(r.ot_pay)}</td>
                      <td className="py-2 text-xs max-w-[160px] truncate">{r.reason || r.notes || "—"}</td>
                      <td className="py-2">
                        <OTApprovalActions ot={r} onDone={() => loadForProject(project.project_id)} />
                      </td>
                      <td className="py-2">
                        <Button size="sm" variant="ghost" data-testid={`ot-delete-${r.ot_id}`}
                          onClick={async () => {
                            await api.delete(`/labor-ot/${r.ot_id}`);
                            toast.success("Deleted");
                            loadForProject(project.project_id);
                          }}
                          className="h-7 w-7 p-0 text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                  {otEntries.length === 0 && (
                    <tr><td colSpan={13} className="py-8 text-center text-sm text-muted-foreground">No overtime logged yet</td></tr>
                  )}
                </tbody>
                {otEntries.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-primary/20">
                      <td colSpan={9} className="py-2 text-right text-xs uppercase font-mono text-muted-foreground">Total OT pay</td>
                      <td className="py-2 cell-num font-bold text-primary">{inr(otEntries.reduce((s, r) => s + Number(r.ot_pay || 0), 0), { compact: true })}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OTAutoDialog({ project, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    worker_name: "", trade: "Glazier",
    monthly_salary: 26000, ot_hours: 2, reason: "",
  });
  const per_day = form.monthly_salary ? form.monthly_salary / 26 : 0;
  const per_hour = per_day ? per_day / 8 : 0;
  const ot_rate = per_hour * 1.25;
  const ot_pay = ot_rate * (Number(form.ot_hours) || 0);
  const save = async () => {
    if (!form.worker_name) return toast.error("Worker name required");
    try {
      await api.post("/labor-ot", {
        project_id: project.project_id, mode: "auto",
        ...form,
        monthly_salary: Number(form.monthly_salary), ot_hours: Number(form.ot_hours),
      });
      toast.success("Overtime added"); setOpen(false); onCreated();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="ot-auto-btn" className="bg-primary hover:bg-primary/90">
          <Calculator className="h-3.5 w-3.5 mr-1" /> Auto OT
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add overtime · auto calc (1.25×)</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></Field>
          <Field label="Worker name"><Input data-testid="ot-worker-name" value={form.worker_name} onChange={(e) => setForm({...form, worker_name: e.target.value})} /></Field>
          <Field label="Trade">
            <Select value={form.trade} onValueChange={(v) => setForm({...form, trade: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{FACADE_TRADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Agreed monthly salary (₹)">
            <Input data-testid="ot-monthly-salary" type="number" value={form.monthly_salary} onChange={(e) => setForm({...form, monthly_salary: e.target.value})} />
          </Field>
          <Field label="Overtime hours">
            <Input data-testid="ot-hours" type="number" step="0.25" value={form.ot_hours} onChange={(e) => setForm({...form, ot_hours: e.target.value})} />
          </Field>
          <Field label="Reason (optional)" full><Textarea rows={2} value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} /></Field>
        </div>
        <div className="rounded-md border bg-muted/40 p-3 text-sm space-y-1 font-mono">
          <div className="flex justify-between"><span className="text-muted-foreground">Per day rate (÷26)</span><span>{inr(per_day)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Per hour rate (÷8)</span><span>{inr(per_hour)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">OT hourly rate (×1.25)</span><span className="text-secondary-foreground">{inr(ot_rate)}</span></div>
          <div className="flex justify-between border-t pt-1 mt-1"><span className="font-medium">Total OT pay</span><span data-testid="ot-preview-total" className="font-bold text-primary">{inr(ot_pay)}</span></div>
        </div>
        <DialogFooter>
          <Button data-testid="ot-auto-save" onClick={save} className="bg-primary hover:bg-primary/90">Save overtime</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OTManualDialog({ project, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    worker_name: "", trade: "Glazier",
    manual_ot_amount: 0, reason: "", notes: "",
  });
  const save = async () => {
    if (!form.worker_name) return toast.error("Worker name required");
    if (!form.reason) return toast.error("Reason is required for manual OT");
    try {
      await api.post("/labor-ot", {
        project_id: project.project_id, mode: "manual",
        ...form, manual_ot_amount: Number(form.manual_ot_amount),
      });
      toast.success("Manual OT saved"); setOpen(false); onCreated();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="ot-manual-btn" className="border-secondary/50">
          <Plus className="h-3.5 w-3.5 mr-1 text-secondary" /> Manual OT
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add manual overtime</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-muted-foreground mb-2 bg-amber-50 border border-amber-200 rounded p-2">
          Use this only when the standard 1.25× formula does not apply — e.g. contract laborers, night shift premium, or special-case bonuses.
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></Field>
          <Field label="Worker name"><Input data-testid="ot-m-worker-name" value={form.worker_name} onChange={(e) => setForm({...form, worker_name: e.target.value})} /></Field>
          <Field label="Trade">
            <Select value={form.trade} onValueChange={(v) => setForm({...form, trade: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{FACADE_TRADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="OT amount (₹)">
            <Input data-testid="ot-m-amount" type="number" value={form.manual_ot_amount} onChange={(e) => setForm({...form, manual_ot_amount: e.target.value})} />
          </Field>
          <Field label="Reason (required)" full>
            <Textarea data-testid="ot-m-reason" rows={2} value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})}
              placeholder="e.g. Contract laborer flat OT rate, night shift bonus…" />
          </Field>
        </div>
        <DialogFooter>
          <Button data-testid="ot-manual-save" onClick={save} className="bg-primary hover:bg-primary/90">Save manual OT</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewLaborDialog({ project, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    gang_name: "", trade: "Glazier", num_workers: 8, rate_per_day: 900, supervisor: "",
  });
  const save = async () => {
    try {
      await api.post("/labor", { project_id: project.project_id, ...form, num_workers: Number(form.num_workers), rate_per_day: Number(form.rate_per_day) });
      toast.success("Labor logged"); setOpen(false); onCreated();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="new-labor-btn" className="bg-primary hover:bg-primary/90"><Plus className="h-3 w-3 mr-1" />Log gang</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Log daily facade gang</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></Field>
          <Field label="Gang name"><Input value={form.gang_name} onChange={(e) => setForm({...form, gang_name: e.target.value})} /></Field>
          <Field label="Trade">
            <Select value={form.trade} onValueChange={(v) => setForm({...form, trade: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{FACADE_TRADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Supervisor"><Input value={form.supervisor} onChange={(e) => setForm({...form, supervisor: e.target.value})} /></Field>
          <Field label="Workers"><Input type="number" value={form.num_workers} onChange={(e) => setForm({...form, num_workers: e.target.value})} /></Field>
          <Field label="Rate / day (₹)"><Input type="number" value={form.rate_per_day} onChange={(e) => setForm({...form, rate_per_day: e.target.value})} /></Field>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewSubDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", trade: "Aluminium Fabrication (YKK)", contact_person: "", phone: "", gst_no: "", pan: "", address: "" });
  const save = async () => {
    try { await api.post("/subcontractors", form); toast.success("Subcontractor added"); setOpen(false); onCreated(); }
    catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="new-sub-btn"><Plus className="h-3 w-3 mr-1" />Add subcontractor</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Add facade subcontractor</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" full><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></Field>
          <Field label="Trade">
            <Select value={form.trade} onValueChange={(v) => setForm({...form, trade: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{SUB_TRADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Contact person"><Input value={form.contact_person} onChange={(e) => setForm({...form, contact_person: e.target.value})} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></Field>
          <Field label="GST no"><Input value={form.gst_no} onChange={(e) => setForm({...form, gst_no: e.target.value})} /></Field>
          <Field label="PAN"><Input value={form.pan} onChange={(e) => setForm({...form, pan: e.target.value})} /></Field>
          <Field label="Address" full><Textarea rows={2} value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></Field>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewSubBillDialog({ project, subs, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    sub_id: "", bill_no: "", date: new Date().toISOString().slice(0, 10),
    work_description: "", amount: 0, gst_pct: 18, tds_pct: 2, retention_pct: 5, status: "pending",
  });
  const save = async () => {
    if (!form.sub_id) return toast.error("Select subcontractor");
    try {
      await api.post("/sub-bills", { project_id: project.project_id, ...form, amount: Number(form.amount) });
      toast.success("Bill added"); setOpen(false); onCreated();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="new-sub-bill-btn" className="bg-primary hover:bg-primary/90"><Plus className="h-3 w-3 mr-1" />New bill</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Subcontractor bill</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Subcontractor" full>
            <Select value={form.sub_id} onValueChange={(v) => setForm({...form, sub_id: v})}>
              <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
              <SelectContent>{subs.map((s) => <SelectItem key={s.sub_id} value={s.sub_id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Bill #"><Input value={form.bill_no} onChange={(e) => setForm({...form, bill_no: e.target.value})} /></Field>
          <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} /></Field>
          <Field label="Amount (₹)"><Input type="number" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} /></Field>
          <Field label="Work" full><Textarea rows={2} value={form.work_description} onChange={(e) => setForm({...form, work_description: e.target.value})} /></Field>
          <Field label="GST %"><Input type="number" value={form.gst_pct} onChange={(e) => setForm({...form, gst_pct: e.target.value})} /></Field>
          <Field label="TDS %"><Input type="number" value={form.tds_pct} onChange={(e) => setForm({...form, tds_pct: e.target.value})} /></Field>
          <Field label="Retention %"><Input type="number" value={form.retention_pct} onChange={(e) => setForm({...form, retention_pct: e.target.value})} /></Field>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewWorkOrderDialog({ project, meta, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    wo_no: `WO-${project.code}-${String(Math.floor(Math.random() * 900) + 100)}`,
    system: meta.systems[0] || "",
    unit_type: meta.unit_types[0] || "",
    quantity: 10, size_mm: "1200W x 1500H",
    glass_spec: "DGU 6+12A+6 Clear", finish: "Anodized Silver",
    status: "design",
    target_dispatch: new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString().slice(0, 10),
    notes: "",
  });
  const save = async () => {
    try {
      await api.post("/work-orders", { project_id: project.project_id, ...form, quantity: Number(form.quantity) });
      toast.success("Work order created");
      setOpen(false); onCreated();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="new-wo-btn" className="bg-primary hover:bg-primary/90"><Plus className="h-3 w-3 mr-1" />New work order</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>New YKK Iwin work order</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="WO #"><Input data-testid="wo-no" value={form.wo_no} onChange={(e) => setForm({...form, wo_no: e.target.value})} /></Field>
          <Field label="Quantity"><Input type="number" value={form.quantity} onChange={(e) => setForm({...form, quantity: e.target.value})} /></Field>
          <Field label="System (YKK Iwin)">
            <Select value={form.system} onValueChange={(v) => setForm({...form, system: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{meta.systems.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Unit type">
            <Select value={form.unit_type} onValueChange={(v) => setForm({...form, unit_type: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{meta.unit_types.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Size (mm)"><Input value={form.size_mm} onChange={(e) => setForm({...form, size_mm: e.target.value})} /></Field>
          <Field label="Glass spec">
            <Select value={form.glass_spec} onValueChange={(v) => setForm({...form, glass_spec: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["DGU 6+12A+6 Clear","DGU 6+12A+6 Low-E","Laminated 6+6 SGP","Tempered 8mm Clear","DGU 8+12A+8 Reflective"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Finish">
            <Select value={form.finish} onValueChange={(v) => setForm({...form, finish: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Anodized Silver","Powder Coat Matte Black","Wood-Grain PPC","Champagne Anodized","Powder Coat RAL 9016 White"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Target dispatch"><Input type="date" value={form.target_dispatch} onChange={(e) => setForm({...form, target_dispatch: e.target.value})} /></Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Notes" full><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></Field>
        </div>
        <DialogFooter><Button data-testid="wo-save" onClick={save} className="bg-primary hover:bg-primary/90">Create WO</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs text-muted-foreground mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

async function _authFetchBlob(path) {
  const token = localStorage.getItem("session_token");
  const r = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) throw new Error("Failed");
  return await r.blob();
}

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

function PlannerButton() {
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(4);
  const download = async () => {
    setBusy(true);
    try {
      const blob = await _authFetchBlob(`/work-orders/planner/pdf?days=${days}`);
      downloadBlob(blob, `WorkPlanner_${new Date().toISOString().slice(0,10)}.pdf`);
      toast.success("Work planner PDF downloaded");
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1">
      <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
        <SelectTrigger data-testid="planner-days" className="h-9 w-36 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="3">Next 3 days (Mon plan)</SelectItem>
          <SelectItem value="4">Next 4 days (Thu plan)</SelectItem>
          <SelectItem value="7">Next 7 days</SelectItem>
          <SelectItem value="14">Next 14 days</SelectItem>
        </SelectContent>
      </Select>
      <Button data-testid="planner-pdf-btn" onClick={download} disabled={busy}
        variant="outline" className="h-9 border-secondary/50 hover:bg-secondary/10">
        <ClipboardList className="h-4 w-4 mr-1.5 text-secondary" />
        {busy ? "Preparing…" : "Work planner PDF"}
      </Button>
    </div>
  );
}

function BOMButton({ wo }) {
  const [open, setOpen] = useState(false);
  const [bom, setBom] = useState(null);
  const load = async () => {
    setOpen(true);
    try {
      const r = await api.get(`/work-orders/${wo.work_order_id}/bom`);
      setBom(r.data);
    } catch { toast.error("BOM load failed"); }
  };
  return (
    <>
      <Button size="sm" variant="outline" data-testid={`bom-${wo.work_order_id}`}
        className="h-7 text-xs" onClick={load}>
        <FileText className="h-3 w-3 mr-1" /> BOM
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Bill of Materials · {wo.wo_no}</DialogTitle>
          </DialogHeader>
          {!bom ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Computing BOM…</div>
          ) : (
            <div className="overflow-x-auto max-h-[70vh]">
              <div className="text-xs text-muted-foreground mb-2 font-mono">
                {bom.system} · {bom.unit_type} · Qty <b>{bom.quantity}</b> · Size {bom.size_mm}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b uppercase font-mono">
                    <th className="py-2 font-medium">Category</th>
                    <th className="py-2 font-medium">Item / SKU</th>
                    <th className="py-2 font-medium">Unit</th>
                    <th className="py-2 font-medium text-right">Per unit</th>
                    <th className="py-2 font-medium text-right">Total for {bom.quantity}</th>
                  </tr>
                </thead>
                <tbody>
                  {bom.rows.map((r, i) => (
                    <tr key={i} className="border-b hover:bg-accent/50">
                      <td className="py-1.5"><Badge variant="outline" className="text-[10px]">{r.category}</Badge></td>
                      <td className="py-1.5">{r.item}</td>
                      <td className="py-1.5 font-mono text-xs">{r.unit}</td>
                      <td className="py-1.5 cell-num text-muted-foreground">{r.qty_per_unit}</td>
                      <td className="py-1.5 cell-num font-medium">{r.total_qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-[11px] text-muted-foreground mt-3">
                Estimates use YKK Iwin profile rules with perimeter/area maths per unit.
                Verify with shop drawings before raising a purchase order.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function LabelButton({ wo }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const blob = await _authFetchBlob(`/work-orders/${wo.work_order_id}/label`);
      downloadBlob(blob, `Label_${wo.wo_no}.pdf`);
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" data-testid={`label-${wo.work_order_id}`}
      className="h-7 text-xs" onClick={download} disabled={busy}>
      <QrCode className="h-3 w-3 mr-1" /> Label
    </Button>
  );
}


const OT_STATUS_META = {
  pending:              { label: "Pending",       cls: "bg-slate-100 text-slate-700 border-slate-200",   icon: Hourglass },
  supervisor_approved:  { label: "Supervisor OK", cls: "bg-sky-100 text-sky-800 border-sky-200",         icon: ShieldCheck },
  approved:             { label: "HR approved",   cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  rejected:             { label: "Rejected",      cls: "bg-red-100 text-red-800 border-red-200",         icon: XCircle },
};

function OTStatusBadge({ r }) {
  const meta = OT_STATUS_META[r.status] || OT_STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <div className="flex items-center gap-1" data-testid={`ot-status-${r.ot_id}`}>
      <Badge className={meta.cls}>
        <Icon className="h-3 w-3 mr-1" /> {meta.label}
      </Badge>
    </div>
  );
}

function OTApprovalActions({ ot, onDone }) {
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");

  const canSup = ot.status === "pending";
  const canHR = ot.status === "supervisor_approved";
  const canReject = ot.status !== "approved" && ot.status !== "rejected";

  const call = async (endpoint, body) => {
    setBusy(true);
    try {
      await api.post(`/labor-ot/${ot.ot_id}/${endpoint}`, body || {});
      toast.success("Updated");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (ot.status === "approved") {
    return (
      <div className="text-[10px] font-mono text-emerald-800 leading-tight" data-testid={`ot-approvals-${ot.ot_id}`}>
        <div className="truncate max-w-[140px]">Sup: {ot.supervisor_approved_by?.split("@")[0] || "—"}</div>
        <div className="truncate max-w-[140px]">HR: {ot.hr_approved_by?.split("@")[0] || "—"}</div>
      </div>
    );
  }
  if (ot.status === "rejected") {
    return (
      <div className="text-[10px] font-mono text-red-800 leading-tight truncate max-w-[140px]" data-testid={`ot-approvals-${ot.ot_id}`}>
        {ot.rejection_reason || "Rejected"}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1" data-testid={`ot-approvals-${ot.ot_id}`}>
      {canSup && (
        <Button size="sm" disabled={busy} data-testid={`ot-sup-approve-${ot.ot_id}`}
          onClick={() => call("supervisor-approve")}
          className="h-6 px-2 text-[10px] bg-sky-600 hover:bg-sky-700 text-white">
          <ShieldCheck className="h-3 w-3 mr-1" /> Supervisor OK
        </Button>
      )}
      {canHR && (
        <Button size="sm" disabled={busy} data-testid={`ot-hr-approve-${ot.ot_id}`}
          onClick={() => call("hr-approve")}
          className="h-6 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white">
          <CheckCircle2 className="h-3 w-3 mr-1" /> HR sign-off
        </Button>
      )}
      {canReject && (
        <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" disabled={busy} data-testid={`ot-reject-${ot.ot_id}`}
              className="h-6 px-2 text-[10px] border-red-200 text-red-700 hover:bg-red-50">
              <XCircle className="h-3 w-3 mr-1" /> Reject
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject overtime for {ot.worker_name}</DialogTitle>
            </DialogHeader>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why?" data-testid={`ot-reject-reason-${ot.ot_id}`} />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setRejectOpen(false)}>Cancel</Button>
              <Button className="bg-destructive hover:bg-destructive/90" data-testid={`ot-reject-confirm-${ot.ot_id}`}
                onClick={async () => { await call("reject", { reason }); setRejectOpen(false); setReason(""); }}>
                Reject
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
